import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";

import { format } from "@gamedev-web/gamedev-sdk-core";
import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";
import { Icon, IconSize } from "@gaming/xds-react-js";
import { CheckInCircleSolid, Clock, Warning } from "@gaming/xds-react-js-icons";

import { LegalEntityNameChangeStatus } from "../../components/LegalEntityNameChangeStatus";
import { Modal } from "../../components/Modal/Modal";
import { PartnerAccountIconImage } from "../../components/PartnerAccountIconImage";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { partnerSectionLinks, xboxOnboardSiteLocationPrefix } from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import type { AdminContact } from "../../models/adminPage";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import {
  XboxOnboardResponse,
  XboxOnboardSubmitRequest,
} from "../../server/crm/api/models/xboxOnboardModels";
import {
  type AdminActionConfirmation,
  AdminActionConfirmationModal,
} from "../AdminPage/components/AdminActionConfirmationModal";
import { TransferPrimarySignatoryModal } from "../AdminPage/components/TransferPrimarySignatoryModal";
import { getAvatarClass, getMemberInitials } from "../AdminPage/avatarHelpers";
import { XboxOnboardForm } from "../XboxOnboardPage/components";
import { LegalEntityNameChangeModal } from "./LegalEntityNameChangeModal";
import * as styles from "./PartnerSettingsPage.module.css";

const settingsFormId = "partner-settings-form";
const settingsDataUrl = `${xboxOnboardSiteLocationPrefix}/settings/api/data`;
const adminContactsUrl = `${xboxOnboardSiteLocationPrefix}/admin/api/contacts`;
const primarySignatoryUrl = `${xboxOnboardSiteLocationPrefix}/primary-signatory`;
const legalEntityNameChangeUrl = `${xboxOnboardSiteLocationPrefix}/settings/api/legal-entity-name-change`;
const legalEntityNameChangePollingDelay = 30000;
const legalEntityNameChangeMimeTypes: Readonly<Record<string, string>> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".pdf": "application/pdf",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const normalizeLegalEntityName = (value: string | undefined): string => value?.trim() ?? "";

const getDocumentMimeType = (document: File): string => {
  const extension = document.name.slice(document.name.lastIndexOf(".")).toLowerCase();
  return document.type || legalEntityNameChangeMimeTypes[extension] || "";
};
const settingsImageUrl = `${xboxOnboardSiteLocationPrefix}/settings/api/image`;
const supportedImageTypes = new Set(["image/bmp", "image/gif", "image/jpeg", "image/png"]);
const maximumImageSize = 10 * 1024 * 1024;

export const PartnerSettingsPage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.PartnerSettings);
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const [data, setData] = useState<XboxOnboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLogo, setPendingLogo] = useState<File>();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>();
  const [logoMessage, setLogoMessage] = useState("");
  const [logoHasError, setLogoHasError] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveAnnouncement, setSaveAnnouncement] = useState("");
  const [hasLegalNameChanged, setHasLegalNameChanged] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [areContactsLoading, setAreContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isTransferringPrimarySignatory, setIsTransferringPrimarySignatory] = useState(false);
  const [primarySignatoryConfirmation, setPrimarySignatoryConfirmation] =
    useState<AdminActionConfirmation | null>(null);
  const [pendingLegalNameRequest, setPendingLegalNameRequest] =
    useState<XboxOnboardSubmitRequest>();

  const [
    settingsLabel,
    overviewLabel,
    cancelLabel,
    saveLabel,
    savingLabel,
    idXboxTitle,
    approvedStatus,
    pendingStatus,
    notStartedStatus,
    viewApplicationLabel,
    contactSupportLabel,
    saveErrorLabel,
    loadingStatus,
    savedStatus,
    supportModalTitle,
    supportModalDescription,
    supportEmail,
    closeLabel,
    requestSubmittedStatus,
    logoLabel,
    logoHint,
    browseLabel,
    uploadingLogoLabel,
    logoUpdatedLabel,
    logoUploadErrorLabel,
    logoInvalidLabel,
    primarySignatoryLabel,
    primarySignatoryDescription,
    changeLabel,
    businessContactLabel,
    youLabel,
    adminRoleLabel,
    gameManagerRoleLabel,
    userRoleLabel,
    primarySignatoryUpdatedFormat,
    primarySignatoryUpdateErrorTitle,
    contactsLoadingLabel,
    contactsErrorLabel,
    ndaSubmitNotice,
  ] = useStrings(
    "partnerHomeSettingsLabel",
    "partnerOverviewPageTitle",
    "cancelButton",
    "partnerSettingsSaveLabel",
    "partnerSettingsSavingLabel",
    "partnerSettingsIdXboxTitle",
    "partnerSettingsIdXboxApprovedStatus",
    "partnerSettingsIdXboxPendingStatus",
    "partnerSettingsIdXboxNotStartedStatus",
    "partnerSettingsViewApplicationLabel",
    "partnerSettingsContactSupportLabel",
    "partnerSettingsSaveError",
    "partnerSettingsLoadingStatus",
    "partnerSettingsSavedStatus",
    "partnerSettingsSupportModalTitle",
    "partnerSettingsSupportModalDescription",
    "partnerSettingsSupportEmail",
    "closeAriaLabel",
    "partnerSettingsLegalNameRequestSubmittedStatus",
    "partnerSettingsLogoLabel",
    "partnerSettingsLogoHint",
    "partnerSettingsLogoBrowseLabel",
    "partnerSettingsLogoUploadingLabel",
    "partnerSettingsLogoUpdatedLabel",
    "partnerSettingsLogoUploadError",
    "partnerSettingsLogoInvalidError",
    "partnerAdminPrimarySignatoryLabel",
    "partnerAdminPrimarySignatoryDescription",
    "partnerMemberChangeRoleLabel",
    "partnerAdminBusinessContactLabel",
    "partnerAdminYouLabel",
    "partnerAdminGameConceptAdminAccessLabel",
    "partnerAdminGameManagerAccessLabel",
    "partnerAdminSubmittedConceptsOnlyAccessLabel",
    "partnerAdminPrimarySignatoryUpdatedFormat",
    "partnerAdminPrimarySignatoryUpdateErrorTitle",
    "partnerAdminLoadingLabel",
    "partnerAdminErrorLabel",
    "partnerSettingsLegalNameNdaSubmitNotice"
  );

  const refreshSettingsData = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const response = await fetch(settingsDataUrl, { signal });
      if (!response.ok) {
        throw new Error(`Failed to load partner settings: ${response.status}`);
      }
      const settings = (await response.json()) as XboxOnboardResponse;
      if (!signal?.aborted) {
        setData(settings);
        dispatch(
          xboxOnboardActions.updateDevelopmentHardwareAccess(
            settings.hasDevelopmentHardwareAccess === true
          )
        );
        setSaveError("");
      }
    },
    [dispatch]
  );
  const handleLegalEntityNameChangeStatusChanged = useCallback(() => {
    refreshSettingsData().catch(() => setSaveError(saveErrorLabel));
  }, [refreshSettingsData, saveErrorLabel]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    refreshSettingsData(controller.signal)
      .catch(() => {
        if (!controller.signal.aborted) {
          setSaveError(saveErrorLabel);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [isSignedIn, refreshSettingsData, saveErrorLabel]);

  useEffect(() => {
    if (!isSignedIn || !data?.accountId || data.isOnboardingHubProfileMode) {
      return;
    }

    const controller = new AbortController();
    setAreContactsLoading(true);
    setContactsError(false);
    fetch(adminContactsUrl, { signal: controller.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load contacts: ${response.status}`);
        }
        return response.json() as Promise<AdminContact[]>;
      })
      .then(response => {
        if (!controller.signal.aborted && Array.isArray(response)) {
          setContacts(response);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setContactsError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAreContactsLoading(false);
        }
      });

    return () => controller.abort();
  }, [data?.accountId, data?.isOnboardingHubProfileMode, isSignedIn]);

  useEffect(() => {
    if (!isSignedIn || !data?.legalEntityNameChange?.hasActiveRequest) {
      return;
    }

    const controller = new AbortController();
    const interval = window.setInterval(() => {
      refreshSettingsData(controller.signal).catch(() => {
        if (!controller.signal.aborted) {
          setSaveError(saveErrorLabel);
        }
      });
    }, legalEntityNameChangePollingDelay);

    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, [
    data?.legalEntityNameChange?.hasActiveRequest,
    isSignedIn,
    refreshSettingsData,
    saveErrorLabel,
  ]);

  useEffect(
    () => () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    },
    [logoPreviewUrl]
  );

  const saveSettingsAndLogo = useCallback(
    async (
      request: XboxOnboardSubmitRequest,
      shouldSaveSettings: boolean
    ): Promise<XboxOnboardResponse | undefined> => {
      let settingsResponse: Response | undefined;
      if (shouldSaveSettings) {
        settingsResponse = await fetch(settingsDataUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        });
        if (!settingsResponse.ok) {
          throw new Error(`Failed to save partner settings: ${settingsResponse.status}`);
        }
      }

      if (pendingLogo) {
        const imageResponse = await fetch(settingsImageUrl, {
          method: "PUT",
          headers: { "Content-Type": pendingLogo.type },
          body: pendingLogo,
        });
        if (!imageResponse.ok) {
          setLogoHasError(true);
          setLogoMessage(logoUploadErrorLabel);
          throw new Error(`Failed to upload partner logo: ${imageResponse.status}`);
        }
        setPendingLogo(undefined);
        setLogoMessage(logoUpdatedLabel);
      }

      return settingsResponse
        ? ((await settingsResponse.json()) as XboxOnboardResponse)
        : undefined;
    },
    [logoUpdatedLabel, logoUploadErrorLabel, pendingLogo]
  );

  const handleSave = useCallback(
    async (request: XboxOnboardSubmitRequest) => {
      const legalNameChanged =
        normalizeLegalEntityName(request.studio.businessName) !==
        normalizeLegalEntityName(data?.studio.businessName);
      if (legalNameChanged && data?.legalEntityNameChange?.canRequest) {
        setPendingLegalNameRequest(request);
        setSaveError("");
        return;
      }

      setIsSaving(true);
      setSaveError("");
      setSaveAnnouncement("");
      try {
        const settings = await saveSettingsAndLogo(request, true);
        if (settings) {
          setData(settings);
          setHasLegalNameChanged(false);
        }
        setSaveAnnouncement(savedStatus);
      } catch {
        setSaveError(saveErrorLabel);
      } finally {
        setIsSaving(false);
      }
    },
    [data, saveErrorLabel, saveSettingsAndLogo, savedStatus]
  );

  const handleLegalNameChange = useCallback(
    async (document?: File, description?: string) => {
      if (!pendingLegalNameRequest || !data?.legalEntityNameChange) {
        return;
      }
      const requiresDocument =
        data.accountType === "aaa" || data.legalEntityNameChange.requiresReview;
      if (requiresDocument && !document) {
        return;
      }

      setIsSaving(true);
      setSaveError("");
      setSaveAnnouncement("");
      try {
        if (data.isOnboardingHubProfileMode) {
          const settings = await saveSettingsAndLogo(pendingLegalNameRequest, true);
          if (settings) {
            setData(settings);
          }
          setHasLegalNameChanged(false);
          const resendResponse = await fetch(`${xboxOnboardSiteLocationPrefix}/resendNda`, {
            method: "POST",
          });
          if (!resendResponse.ok) {
            throw new Error(`Failed to resend NDA: ${resendResponse.status}`);
          }
          await refreshSettingsData();
        } else {
          await saveSettingsAndLogo(
            pendingLegalNameRequest,
            data.hubPermissions.canEditOwnContactDetails || data.hubPermissions.canEditStudioDetails
          );

          const legalEntityName = pendingLegalNameRequest.studio.businessName.trim();
          let legalNameResponse: Response;
          if (document) {
            const descriptionQuery = description
              ? `&description=${encodeURIComponent(description)}`
              : "";
            legalNameResponse = await fetch(
              `${legalEntityNameChangeUrl}/review?legalEntityName=${encodeURIComponent(
                legalEntityName
              )}${descriptionQuery}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": getDocumentMimeType(document),
                  "X-File-Name": encodeURIComponent(document.name),
                },
                body: document,
              }
            );
          } else {
            legalNameResponse = await fetch(legalEntityNameChangeUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ legalEntityName }),
            });
          }
          if (!legalNameResponse.ok) {
            throw new Error(`Failed to change legal entity name: ${legalNameResponse.status}`);
          }
          setData((await legalNameResponse.json()) as XboxOnboardResponse);
        }
        setSaveAnnouncement(requestSubmittedStatus);
        setPendingLegalNameRequest(undefined);
      } catch {
        setSaveError(saveErrorLabel);
      } finally {
        setIsSaving(false);
      }
    },
    [
      data,
      pendingLegalNameRequest,
      refreshSettingsData,
      requestSubmittedStatus,
      saveErrorLabel,
      saveSettingsAndLogo,
    ]
  );

  const handleLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) {
        return;
      }
      if (!supportedImageTypes.has(file.type) || file.size > maximumImageSize) {
        setLogoHasError(true);
        setLogoMessage(logoInvalidLabel);
        return;
      }

      setPendingLogo(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
      setLogoHasError(false);
      setLogoMessage("");
    },
    [logoInvalidLabel]
  );

  const handleBusinessNameChange = useCallback(
    (businessName: string) => {
      setHasLegalNameChanged(
        normalizeLegalEntityName(businessName) !==
          normalizeLegalEntityName(data?.studio.businessName)
      );
    },
    [data?.studio.businessName]
  );

  const handlePrimarySignatoryTransfer = useCallback(
    async (contact: AdminContact) => {
      setIsTransferringPrimarySignatory(true);
      setSaveError("");
      try {
        const response = await fetch(primarySignatoryUrl, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contactId: contact.contactId }),
        });
        if (!response.ok) {
          throw new Error(`Failed to update primary signatory: ${response.status}`);
        }
        setContacts(current =>
          current.map(item => ({
            ...item,
            isPrimarySignatory: item.contactId === contact.contactId,
          }))
        );
        const name = `${contact.firstName} ${contact.lastName}`.trim() || contact.email;
        setSaveAnnouncement(format(primarySignatoryUpdatedFormat, name));
        setPrimarySignatoryConfirmation(null);
      } catch {
        setSaveError(primarySignatoryUpdateErrorTitle);
      } finally {
        setIsTransferringPrimarySignatory(false);
      }
    },
    [primarySignatoryUpdateErrorTitle, primarySignatoryUpdatedFormat]
  );

  if (!isPageReady || !isSignedIn || isLoading) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={settingsLabel}
        breadcrumbSteps={[{ label: overviewLabel, to: partnerSectionLinks.overview.to }]}
      >
        <div role="status" aria-live="polite" aria-label={loadingStatus} aria-busy="true">
          <PageLoadingSpinner />
        </div>
      </PartnerPageLayout>
    );
  }

  if (!data) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={settingsLabel}
        breadcrumbSteps={[{ label: overviewLabel, to: partnerSectionLinks.overview.to }]}
      >
        <p className={styles.errorMessage} role="alert">
          {saveError || saveErrorLabel}
        </p>
      </PartnerPageLayout>
    );
  }

  const applicationStatus = data.isIdXboxApproved
    ? approvedStatus.replace("{0}", data.studio.studioName ?? "")
    : data.hasStartedIdXboxApplication
    ? pendingStatus
    : notStartedStatus;
  const isAaaUser = data.accountType === "aaa";
  const hasPartnerAccount = Boolean(data.accountId) && !data.isOnboardingHubProfileMode;
  const canEditOwnContactDetails = data.hubPermissions.canEditOwnContactDetails;
  const canEditStudioDetails =
    data.isOnboardingHubProfileMode || data.hubPermissions.canEditStudioDetails;
  const canEditLegalEntityName = data.legalEntityNameChange?.canRequest === true;
  const canEditPartnerLogo = data.hubPermissions.canAccessAdministratorSettings;
  const canChangePrimarySignatory = data.hubPermissions.canChangePrimarySignatory;
  const canEditSettings =
    canEditOwnContactDetails || canEditStudioDetails || canEditLegalEntityName;
  const canInteractWithLegalEntityNameStatus =
    data.legalEntityNameChange?.canCancelInquiry === true ||
    data.legalEntityNameChange?.canResendNda === true;
  const primarySignatory = contacts.find(contact => contact.isPrimarySignatory);
  const primarySignatoryRole =
    primarySignatory?.hubRole === HubAccessLevel.Admin
      ? adminRoleLabel
      : primarySignatory?.hubRole === HubAccessLevel.GameManager
      ? gameManagerRoleLabel
      : userRoleLabel;
  const primarySignatoryTargets = contacts.filter(
    contact => contact.isAccountContact !== false && !contact.isPrimarySignatory
  );
  const legalEntityNameChangeStatus =
    data.hubPermissions.canAccessAdministratorSettings &&
    data.legalEntityNameChange?.hasActiveRequest ? (
      <LegalEntityNameChangeStatus
        state={{
          ...data.legalEntityNameChange,
          currentLegalEntityName:
            data.legalEntityNameChange.currentLegalEntityName ?? data.studio.businessName,
        }}
        contactEmail={data.contact.email}
        onStatusChanged={handleLegalEntityNameChangeStatusChanged}
      />
    ) : undefined;
  return (
    <>
      <PartnerPageLayout
        breadcrumbLabel={settingsLabel}
        breadcrumbSteps={[{ label: overviewLabel, to: partnerSectionLinks.overview.to }]}
      >
        <div aria-busy={isSaving}>
          <span
            className={styles.screenReaderOnly}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {isSaving ? savingLabel : saveAnnouncement}
          </span>
          <div className={styles.header}>
            {hasLegalNameChanged && canEditLegalEntityName && !data.isOnboardingHubProfileMode && (
              <div className={styles.legalNameWarning} role="status">
                <Icon
                  aria-hidden="true"
                  className={styles.legalNameWarningIcon}
                  icon={Warning}
                  iconSize={IconSize.Custom}
                />
                <div className={styles.legalNameWarningText}>
                  <strong>{ndaSubmitNotice}</strong>
                </div>
              </div>
            )}
            <div className={styles.headerActions}>
              <button
                type="button"
                className={styles.cancelButton}
                disabled={isSaving}
                onClick={() => history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`)}
              >
                {cancelLabel}
              </button>
              {canEditSettings && (
                <button
                  type="submit"
                  form={settingsFormId}
                  className={styles.saveButton}
                  disabled={isSaving || !isFormValid}
                >
                  {isSaving ? savingLabel : saveLabel}
                </button>
              )}
            </div>
          </div>

          {saveError && (
            <p className={styles.errorMessage} role="alert">
              {saveError}
            </p>
          )}

          <div className={styles.content}>
            <fieldset
              className={styles.formColumn}
              disabled={!canEditSettings && !canInteractWithLegalEntityNameStatus}
            >
              <XboxOnboardForm
                data={data}
                isSubmitting={isSaving}
                formId={settingsFormId}
                showActions={false}
                lockBusinessIdentityFields
                canEditLegalEntityName={canEditLegalEntityName}
                canEditOwnContactDetails={canEditOwnContactDetails}
                canEditStudioDetails={canEditStudioDetails}
                partnerLogo={
                  data.isOnboardingHubProfileMode ? undefined : (
                    <div className={styles.logoEditor}>
                      <span className={styles.logoLabel}>{logoLabel}</span>
                      <label
                        className={`${styles.logoPreview} ${
                          !canEditPartnerLogo || isSaving ? styles.logoPreviewDisabled : ""
                        }`}
                      >
                        <PartnerAccountIconImage
                          key={logoPreviewUrl ?? "saved-logo"}
                          src={logoPreviewUrl}
                        />
                        <input
                          className={styles.logoInput}
                          type="file"
                          accept="image/bmp,image/gif,image/jpeg,image/png"
                          disabled={!canEditPartnerLogo || isSaving}
                          onChange={handleLogoChange}
                        />
                        <span className={styles.logoOverlay}>
                          {isSaving && pendingLogo ? uploadingLogoLabel : browseLabel}
                        </span>
                      </label>
                      <span className={styles.logoHint}>{logoHint}</span>
                      {logoMessage && (
                        <span
                          className={logoHasError ? styles.logoError : styles.logoStatus}
                          role={logoHasError ? "alert" : "status"}
                        >
                          {logoMessage}
                        </span>
                      )}
                    </div>
                  )
                }
                isOnboardingFunnel={false}
                onSubmitRequest={handleSave}
                onValidityChange={setIsFormValid}
                onBusinessNameChange={handleBusinessNameChange}
                legalEntityNameStatusWarning={legalEntityNameChangeStatus}
                showLegalEntityNameDocumentationWarning={
                  !data.isOnboardingHubProfileMode && canEditLegalEntityName
                }
                businessIdentityFooter={
                  hasPartnerAccount ? (
                    <section className={styles.primarySignatorySection}>
                      <div className={styles.primarySignatoryHeader}>
                        <div>
                          <h2 className={styles.primarySignatoryTitle}>{primarySignatoryLabel}</h2>
                          <p className={styles.primarySignatoryDescription}>
                            {primarySignatoryDescription}
                          </p>
                        </div>
                        {primarySignatory &&
                          canChangePrimarySignatory &&
                          primarySignatoryTargets.length > 0 && (
                            <button
                              type="button"
                              className={styles.primarySignatoryChange}
                              disabled={isTransferringPrimarySignatory}
                              onClick={() => setIsTransferOpen(true)}
                            >
                              {changeLabel}
                            </button>
                          )}
                      </div>
                      {areContactsLoading ? (
                        <p className={styles.primarySignatoryMessage} role="status">
                          {contactsLoadingLabel}
                        </p>
                      ) : contactsError ? (
                        <p className={styles.primarySignatoryMessage} role="alert">
                          {contactsErrorLabel}
                        </p>
                      ) : (
                        primarySignatory && (
                          <div className={styles.primarySignatoryCard}>
                            <div
                              className={`${styles.primarySignatoryAvatar} ${getAvatarClass(
                                primarySignatory.contactId
                              )}`}
                              aria-hidden="true"
                            >
                              {getMemberInitials(primarySignatory)}
                            </div>
                            <div className={styles.primarySignatoryIdentity}>
                              <strong>
                                {`${primarySignatory.firstName} ${primarySignatory.lastName}`.trim() ||
                                  primarySignatory.email}
                                {primarySignatory.isCurrentUser && (
                                  <span className={styles.primarySignatoryCurrentUser}>
                                    {" "}
                                    ({youLabel})
                                  </span>
                                )}
                              </strong>
                              <span>{primarySignatory.email}</span>
                            </div>
                            <span className={styles.primarySignatoryRole}>
                              {primarySignatory.isHubEnabled
                                ? primarySignatoryRole
                                : businessContactLabel}
                            </span>
                          </div>
                        )
                      )}
                    </section>
                  ) : undefined
                }
              />
            </fieldset>

            {!isAaaUser && !data.isOnboardingHubProfileMode && (
              <aside className={styles.statusCard}>
                <div className={styles.statusHeader}>
                  <h2 className={styles.statusTitle}>{idXboxTitle}</h2>
                  <Icon
                    aria-hidden="true"
                    className={data.isIdXboxApproved ? styles.approvedIcon : styles.pendingIcon}
                    icon={data.isIdXboxApproved ? CheckInCircleSolid : Clock}
                    iconSize={IconSize.Custom}
                  />
                </div>
                <p className={styles.statusDescription}>{applicationStatus}</p>
                <div className={styles.statusLinks}>
                  {data.hubPermissions.canSubmitIdApplication && (
                    <Link className={styles.statusLink} to={`${xboxOnboardSiteLocationPrefix}/id`}>
                      <span>{viewApplicationLabel}</span>
                    </Link>
                  )}
                  <button
                    type="button"
                    className={`${styles.statusLink} ${styles.statusButton}`}
                    onClick={() => setIsSupportModalOpen(true)}
                  >
                    <span>{contactSupportLabel}</span>
                  </button>
                </div>
              </aside>
            )}
          </div>
        </div>
      </PartnerPageLayout>
      <Modal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        width={560}
        ariaLabelledBy="partner-settings-support-title"
      >
        <div className={styles.supportModal}>
          <div className={styles.supportModalHeader}>
            <h2 id="partner-settings-support-title" className={styles.supportModalTitle}>
              {supportModalTitle}
            </h2>
            <button
              type="button"
              className={styles.supportModalClose}
              onClick={() => setIsSupportModalOpen(false)}
              aria-label={closeLabel}
            >
              &#x2715;
            </button>
          </div>
          <p className={styles.supportModalDescription}>{supportModalDescription}</p>
          <a className={styles.supportEmail} href={`mailto:${supportEmail}`}>
            {supportEmail}
          </a>
        </div>
      </Modal>
      <LegalEntityNameChangeModal
        isOpen={pendingLegalNameRequest !== undefined}
        requiresDocument={isAaaUser || data.legalEntityNameChange?.requiresReview === true}
        requiresReview={data.legalEntityNameChange?.requiresReview === true}
        isSubmitting={isSaving}
        error={pendingLegalNameRequest ? saveError : ""}
        allowedExtensions={data.legalEntityNameChange?.allowedSupportingDocumentExtensions ?? []}
        maxDocumentBytes={data.legalEntityNameChange?.maxSupportingDocumentBytes ?? 0}
        onConfirm={handleLegalNameChange}
        onCancel={() => {
          setPendingLegalNameRequest(undefined);
          setSaveError("");
        }}
      />
      <TransferPrimarySignatoryModal
        contacts={primarySignatoryTargets}
        isBusy={isTransferringPrimarySignatory}
        isOpen={isTransferOpen}
        onCancel={() => setIsTransferOpen(false)}
        onSelect={contact => {
          setIsTransferOpen(false);
          setPrimarySignatoryConfirmation({
            action: "transferPrimarySignatory",
            member: contact,
          });
        }}
      />
      <AdminActionConfirmationModal
        confirmation={primarySignatoryConfirmation}
        isBusy={isTransferringPrimarySignatory}
        onCancel={() => setPrimarySignatoryConfirmation(null)}
        onConfirm={confirmation => {
          if (confirmation.action === "transferPrimarySignatory") {
            handlePrimarySignatoryTransfer(confirmation.member);
          }
        }}
      />
    </>
  );
};
